import React from 'react';
import axios from 'axios';
import { NodeProps, useReactFlow } from 'reactflow';
import { useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faClipboard } from '@fortawesome/free-solid-svg-icons';
import CopyToClipboard from 'react-copy-to-clipboard';
import { IoCheckmarkCircleOutline, IoWarningOutline } from 'react-icons/io5';
import { operationType } from '../../../../../services/types';
import { updateNodeValue } from '../../../../../services/method';
import { webURL } from '../../../../../services/API';
import { RootState } from '../../../../../redux/store';
import { ExpandedBlock } from '../../../expanded';
import { SettingsBlock, settingsDataType } from '../../../../settingsBlock';
import { AuthenticateSelect } from '../../../../auth/select';
import { Loader, Main, Styled } from '../easyOrdersTrigger/style';

type BostaTriggerProps = NodeProps<operationType> & {
  setChildMinimize?: (fn: (id: string) => void) => void;
  setChildSettings?: (fn: (id: string) => void) => void;
};

export function BostaTrigger({ id, data, setChildMinimize, setChildSettings }: BostaTriggerProps) {
  const { setNodes } = useReactFlow();
  const [searchParams] = useSearchParams();
  const userToken = useSelector((state: RootState) => state.user.data?.token);
  const projectId = searchParams.get('id');

  const [settings, setSettings] = React.useState<settingsDataType>({ isToggle: false });
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [isCopy, setIsCopy] = React.useState<boolean>(false);
  const [link, setLink] = React.useState<string>('');
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string>('');
  const [isWebhookUrlSectionOpen, setIsWebhookUrlSectionOpen] = React.useState<boolean>(true);

  React.useEffect(() => {
    if (setChildMinimize) setChildMinimize(() => { setIsExpanded(prev => !prev); });
    if (setChildSettings) setChildSettings(() => { setSettings(prev => ({ ...prev, isToggle: !prev.isToggle })); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setChildMinimize, setChildSettings]);

  React.useEffect(() => {
    if (data?.triggerCredentials?.webhookUrl) {
      setLink(data.triggerCredentials.webhookUrl);
      setError('');
    }
  }, [data?.triggerCredentials?.webhookUrl]);

  const handleAuthenticate = async (type: 'add' | 'remove', connectionData: any) => {
    if (type === 'add') {
      updateNodeValue(id, setNodes, { connection: connectionData });
    } else {
      updateNodeValue(id, setNodes, { connection: null });
    }
  };

  const handleHeaderChange = (e: React.ChangeEvent<HTMLInputElement>, key: string) => {
    updateNodeValue(id, setNodes, { [key]: e.target.value });
  };

  const generateLink = async () => {
    setIsLoading(true);
    setError('');

    try {
      if (!projectId) throw new Error('Missing project id');
      if (!userToken) throw new Error('Missing auth token');
      if (!data.value?.connection?.id) throw new Error('Please select a Bosta connection first');

      const response = await axios.post(
        `${webURL}/triggers/bosta/generate-link`,
        {
          nodeIndex: data.index,
          projectId,
          connectionId: data.value?.connection?.id,
          webhookCustomHeaderName: data.value?.webhookCustomHeaderName,
          webhookCustomHeaderValue: data.value?.webhookCustomHeaderValue,
        },
        {
          headers: {
            'x-auth-token': userToken,
          },
        }
      );

      const { nodeId, webhookUrl, blockId } = response.data;
      setNodes(prev =>
        prev.map(node =>
          node.data.index === data.index
            ? {
                ...node,
                data: {
                  ...node.data,
                  triggerCredentials: { nodeId, webhookUrl, blockId },
                },
              }
            : node
        )
      );

      setLink(webhookUrl || '');
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to generate webhook URL';
      setError(message.includes('Trigger node does not exist')
        ? 'Please SAVE the project first (Ctrl+S), then reopen this panel.'
        : message
      );
    } finally {
      setIsLoading(false);
    }
  };

  const isWarning = !data.value?.connection;

  return (
    <>
      <SettingsBlock nodeId={id} data={settings} setData={setSettings} />
      <Main>
        {isWarning ? (
          <>
            <div id="warning"></div>
            <span id="warningIcon"><IoWarningOutline size={21} /></span>
          </>
        ) : data.output ? (
          <>
            <div id="ran"></div>
            <span id="ranIcon"><IoCheckmarkCircleOutline size={21} /></span>
          </>
        ) : null}

        {isExpanded ? (
          <ExpandedBlock
            data={{ id, ...data }}
            handleClose={() => setIsExpanded(false)}
            isWarning={isWarning}
            style={{ width: '480px' }}
          >
            {isLoading ? <Loader /> : (
              <Styled>
                <div className="action">
                  <AuthenticateSelect
                    type="bosta"
                    defaultId={data.value?.connection?.id}
                    handleAuthenticate={handleAuthenticate}
                    background={data.background}
                  />
                </div>

                <div className="toolbar">
                  <button
                    className="generateBtn"
                    onClick={() => { void generateLink(); }}
                    disabled={!data.value?.connection || isLoading}
                    style={{ background: data.background }}
                  >
                    Generate Link
                  </button>
                </div>

                {isWarning ? <div className="warning">Select a Bosta connection to generate URL.</div> : null}
                {error ? <div className="error">{error}</div> : null}

                <div className="sectionCard">
                  <button
                    type="button"
                    className="sectionToggle"
                    onClick={() => setIsWebhookUrlSectionOpen(prev => !prev)}
                  >
                    <span>Bosta Webhook URL</span>
                    <span className="sectionToggleIcon">{isWebhookUrlSectionOpen ? '-' : '+'}</span>
                  </button>

                  {isWebhookUrlSectionOpen ? (
                    <div className="webhookPanelBody">
                      <div className="webhookGroup">
                        <div className="groupTitle">Webhook URL</div>
                        <div className="secret-hint">
                          Paste this URL into Bosta Dashboard API Integration webhook settings.
                        </div>
                        {link ? (
                          <CopyToClipboard
                            text={link}
                            onCopy={() => {
                              setIsCopy(true);
                              setTimeout(() => setIsCopy(false), 500);
                            }}
                          >
                            <div className="link-container" title={link}>
                              <div className="link-with-copy">
                                <FontAwesomeIcon
                                  icon={isCopy ? faCheck : faClipboard}
                                  className={`copy-icon ${isCopy ? 'copied' : ''}`}
                                />
                                <span className="link-text">{link}</span>
                              </div>
                            </div>
                          </CopyToClipboard>
                        ) : (
                          <div className="warning">Generate webhook URL first.</div>
                        )}
                      </div>

                      <div className="webhookGroup">
                        <div className="groupTitle">Custom Authorization Header</div>
                        <div className="secret-hint">
                          Optional. If configured in Bosta, enter both the custom header name and value here.
                        </div>
                        <input
                          className="secret-input"
                          value={data.value?.webhookCustomHeaderName || ''}
                          placeholder="Authorization"
                          onChange={(event) => handleHeaderChange(event, 'webhookCustomHeaderName')}
                        />
                        <input
                          className="secret-input"
                          value={data.value?.webhookCustomHeaderValue || ''}
                          placeholder="Basic abc123"
                          onChange={(event) => handleHeaderChange(event, 'webhookCustomHeaderValue')}
                        />
                      </div>
                    </div>
                  ) : null}
                </div>
              </Styled>
            )}
          </ExpandedBlock>
        ) : null}
      </Main>
    </>
  );
}
